import { useCallback, useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { AgentCategory } from "../types";

interface CategoryFilterProps {
  categories: AgentCategory[];
  selectedCategory: AgentCategory | null;
  onCategoryChange: (category: AgentCategory | null) => void;
}

export const CategoryFilter = ({
  categories,
  selectedCategory,
  onCategoryChange,
}: CategoryFilterProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showLeftFade, setShowLeftFade] = useState(false);
  const [showRightFade, setShowRightFade] = useState(false);

  // Check scroll position to show/hide fades
  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    const { scrollLeft, scrollWidth, clientWidth } = el;
    const isScrollable = scrollWidth > clientWidth;

    setShowLeftFade(scrollLeft > 0);
    setShowRightFade(isScrollable && scrollLeft < scrollWidth - clientWidth - 1);
  }, []);

  // Check on mount and when content changes
  useEffect(() => {
    checkScroll();
    window.addEventListener("resize", checkScroll);
    return () => window.removeEventListener("resize", checkScroll);
  }, [checkScroll, categories]);

  return (
    <div className="relative sticky top-0 z-10 bg-[#f9f9f9] rounded-t-2xl px-4 md:px-6 pt-6 pb-4">
      {/* Scrollable container */}
      <div
        ref={scrollRef}
        onScroll={checkScroll}
        className="flex gap-2 overflow-x-auto scrollbar-hide"
      >
        <Button
          variant={selectedCategory === null ? "default" : "outline"}
          size="sm"
          onClick={() => onCategoryChange(null)}
          className={cn(
            "rounded-full flex-shrink-0 px-5",
            selectedCategory === null &&
              "bg-gray-900 text-white hover:bg-gray-800",
          )}
        >
          All
        </Button>
        {categories.map((category) => (
          <Button
            key={category}
            variant={selectedCategory === category ? "default" : "outline"}
            size="sm"
            onClick={() => onCategoryChange(category)}
            className={cn(
              "rounded-full flex-shrink-0 whitespace-nowrap px-5",
              selectedCategory === category &&
                "bg-gray-900 text-white hover:bg-gray-800",
            )}
          >
            {category}
          </Button>
        ))}
      </div>

      {/* Left fade gradient */}
      <div
        className={cn(
          "absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-[#f9f9f9] to-transparent pointer-events-none rounded-tl-2xl transition-opacity duration-200",
          showLeftFade ? "opacity-100" : "opacity-0",
        )}
      />
      {/* Right fade gradient */}
      <div
        className={cn(
          "absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-[#f9f9f9] to-transparent pointer-events-none rounded-tr-2xl transition-opacity duration-200",
          showRightFade ? "opacity-100" : "opacity-0",
        )}
      />
    </div>
  );
};
